import { Component, OnInit, ViewChild, TemplateRef, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatTableModule, MatTableDataSource } from '@angular/material/table';
import { MatIconModule } from "@angular/material/icon";
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { Subject } from 'rxjs';
import { switchMap, takeUntil } from 'rxjs/operators';

import { CoreService } from '../../../services/core.service';
import { ManageApplicationNavComponent } from '../manage-application-nav/manage-application-nav.component';
import { UserFormComponent } from '../../../components/forms/create-account/user-form/user-form.component';
import { DialogComponent } from '../../../components/dialog/dialog.component';

@Component({
  selector: 'app-index-users',
  standalone: true,
  imports: [
    CommonModule,
    MatPaginatorModule,
    MatTableModule,
    MatIconModule,
    MatTooltipModule,
    MatButtonModule,
    MatSortModule,
    FormsModule,
    ReactiveFormsModule,
    MatInputModule,
    MatDialogModule,
    ManageApplicationNavComponent,
    UserFormComponent
  ],
  templateUrl: './index-users.component.html',
  styleUrl: './index-users.component.scss'
})
export class ManageApplicationIndexUsersComponent implements OnInit, OnDestroy {
  @ViewChild('userFormTmpl', { static: true }) userFormTmpl!: TemplateRef<any>;
  @ViewChild('confirmDeleteTmpl', { static: true }) confirmDeleteTmpl!: TemplateRef<any>;
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  dataSource = new MatTableDataSource<any>([]);
  displayedColumns: string[] = ['ID', 'name', 'email', 'role', 'status', 'created_at', 'actions'];

  searchValue = '';
  totalItems = 0;
  pageSize = 12;
  currentPage = 1;
  isLoading = true;
  hasLoaded = false;
  selectedUser: any = {};

  sortField = 'id';
  sortDirection = 'desc';

  private destroy$ = new Subject<void>();
  private loadTrigger$ = new Subject<number>(); // emits page numbers

  constructor(
    private coreService: CoreService,
    public dialog: MatDialog
  ) {}

  ngOnInit() {
    // centralize loading here; switchMap cancels previous HTTP call
    this.loadTrigger$
      .pipe(
        takeUntil(this.destroy$),
        switchMap((page: number) => {
          this.isLoading = true;

          return this.coreService.getData('/manageAccount/all-users/index', {
            page,
            keyword: this.searchValue,
            sort: this.sortField,
            direction: this.sortDirection,
            per_page: this.pageSize
          });
        })
      )
      .subscribe({
        next: (res: any) => {
          this.dataSource.data = res.data || [];
          this.totalItems = res.total || 0;
          this.pageSize = res.per_page || 12;
          this.currentPage = res.current_page || this.currentPage;
          this.hasLoaded = true;

          if (this.paginator) {
            this.paginator.pageIndex = this.currentPage - 1;
            this.paginator.length = this.totalItems;
            this.paginator.pageSize = this.pageSize;
          }

          this.isLoading = false;
        },
        error: (err) => {
          console.error('Load users error:', err);
          this.dataSource.data = [];
          this.hasLoaded = true;
          this.isLoading = false;
        }
      });

    // initial load
    this.loadUsers(1);
  }

  private loadUsers(page: number = 1) {
    this.loadTrigger$.next(page);
  }

  onSearchSubmit(value: string) {
    this.searchValue = value.trim();
    this.currentPage = 1;

    if (this.paginator) {
      this.paginator.pageIndex = 0;
    }

    this.loadUsers(1);
  }

  clearSearch() {
    this.searchValue = '';
    this.currentPage = 1;

    if (this.paginator) {
      this.paginator.pageIndex = 0;
    }

    this.loadUsers(1);
  }

  onPageChange(event: any) {
    const newPage = event.pageIndex + 1;
    this.currentPage = newPage;
    this.loadUsers(newPage);
  }

  onSortChange(sort: Sort) {
    this.sortField = sort.active === 'ID' ? 'id' : sort.active;
    this.sortDirection = sort.direction || 'desc';
    this.currentPage = 1;

    if (this.paginator) {
      this.paginator.pageIndex = 0;
    }

    this.loadUsers(1);
  }

  addNewUser() {
    this.selectedUser = {};
    this.dialog.open(DialogComponent, {
      data: {
        content: this.userFormTmpl,
        header: 'Add New User',
        cls: '!bg-slate-600 !text-white',
      },
      width: '760px',
      maxWidth: '760px',
    });
  }

  editUser(row: any) {
    this.selectedUser = row;
    this.dialog.open(DialogComponent, {
      data: {
        content: this.userFormTmpl,
        header: 'Edit User',
        cls: '!bg-slate-600 !text-white',
      },
      width: '760px',
      maxWidth: '760px',
    });
  }

  openDeleteConfirm(row: any) {
    this.selectedUser = row;
    this.dialog.open(DialogComponent, {
      data: {
        content: this.confirmDeleteTmpl,
        header: 'Delete User',
        cls: 'bg-red-500 !text-white',
      },
    });
  }

  deleteUser() {
    this.coreService
      .deleteData('msuser/destroy', String(this.selectedUser.ID))
      .subscribe({
        next: () => {
          this.dialog.closeAll();
          this.coreService.showSnackbar('User deleted successfully');
          this.loadUsers(this.currentPage);
        },
        error: (err) => {
          console.error('Delete user error:', err);
          this.dialog.closeAll();
          this.coreService.showSnackbar('Failed to delete user');
        }
      });
  }

  onSubmitUserClbk() {
    this.dialog.closeAll();
    this.loadUsers(this.currentPage);
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
